import React, { useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, QuadraticBezierLine, ContactShadows, Environment } from "@react-three/drei";
import Router3D from "@/components/Router3D";
import DeviceNode3D from "@/components/DeviceNode3D";
import { customLabel } from "@/lib/labels";
import { isFlagged } from "@/lib/flags";

const SUBNET_COLORS = ["#0055FF", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6"];

function ringPositions(count, radius, cx, cz) {
  const pts = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / Math.max(count, 1)) * Math.PI * 2;
    const r = radius + (i % 2 === 0 ? 0 : 0.7);
    pts.push([cx + Math.cos(angle) * r, 0, cz + Math.sin(angle) * r]);
  }
  return pts;
}

export default function NetworkScene({ router, devices, selected, onSelect, newIps, onReady, labels, flags }) {
  const groups = useMemo(() => {
    const primaryKey =
      devices.find((d) => d.ip === router?.ip)?.subnet || devices[0]?.subnet || "net";
    const map = new Map();
    devices.forEach((d) => {
      const k = d.subnet || "net";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    });
    const keys = [...map.keys()].sort((a, b) =>
      a === primaryKey ? -1 : b === primaryKey ? 1 : a.localeCompare(b)
    );
    return { primaryKey, keys, map };
  }, [devices, router]);

  const label = (d) => customLabel(d, labels);
  const secondaryKeys = groups.keys.filter((k) => k !== groups.primaryKey);
  const hubR = 6.5;

  const renderDevice = (d, pos, color) => (
    <React.Fragment key={d.ip}>
      <QuadraticBezierLine
        start={[0, 0, 0]}
        end={pos}
        mid={[pos[0] / 2, 1.0, pos[2] / 2]}
        color={selected && selected.ip === d.ip ? color : "#B9C0CC"}
        lineWidth={selected && selected.ip === d.ip ? 2.6 : 1.3}
        transparent
        opacity={0.85}
      />
      <DeviceNode3D
        device={d}
        position={pos}
        onSelect={onSelect}
        selected={selected && selected.ip === d.ip}
        isNew={newIps && newIps.has(d.ip)}
        label={label(d)}
        flagged={isFlagged(d, flags)}
      />
    </React.Fragment>
  );

  // primary group (around origin)
  const primaryDevices = (groups.map.get(groups.primaryKey) || []).filter(
    (d) => d.ip !== router?.ip
  );
  const primaryPositions = ringPositions(primaryDevices.length, 3.4, 0, 0);

  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      gl={{ preserveDrawingBuffer: true, antialias: true }}
      camera={{ position: [0, 6, 9.5], fov: 45 }}
      onCreated={({ gl }) => onReady && onReady(gl.domElement)}
      onPointerMissed={() => onSelect(null)}
    >
      <color attach="background" args={["#F4F5F7"]} />
      <ambientLight intensity={0.85} />
      <directionalLight
        position={[6, 10, 6]}
        intensity={1.1}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <directionalLight position={[-6, 4, -4]} intensity={0.35} />
      <Environment preset="city" />

      {/* Primary router hub */}
      <Router3D router={router} label={label({ ip: router?.ip, mac: router?.mac })} />
      {primaryDevices.map((d, i) => renderDevice(d, primaryPositions[i], "#0055FF"))}

      {/* Secondary subnet hubs */}
      {secondaryKeys.map((key, gi) => {
        const color = SUBNET_COLORS[(gi + 1) % SUBNET_COLORS.length];
        const angle = (gi / Math.max(secondaryKeys.length, 1)) * Math.PI * 2;
        const hubPos = [Math.cos(angle) * hubR, 0, Math.sin(angle) * hubR];
        const groupDevices = groups.map.get(key) || [];
        const hubDevice = groupDevices.find((d) => d.is_router) || null;
        const others = groupDevices.filter((d) => d !== hubDevice);
        const positions = ringPositions(others.length, 2.0, hubPos[0], hubPos[2]);
        const hubRouter = hubDevice
          ? {
              ip: hubDevice.ip,
              mac: hubDevice.mac,
              vendor: hubDevice.vendor,
              model_label: hubDevice.vendor || hubDevice.hostname || "Sub-router",
            }
          : { ip: null, model_label: key };

        return (
          <group key={key}>
            {/* trunk cable primary -> hub */}
            <QuadraticBezierLine
              start={[0, 0, 0]}
              end={hubPos}
              mid={[hubPos[0] / 2, 1.6, hubPos[2] / 2]}
              color={color}
              lineWidth={3}
              transparent
              opacity={0.9}
            />
            <group position={hubPos} scale={0.62}>
              <Router3D
                router={hubRouter}
                label={hubDevice ? label(hubDevice) : null}
                secondary
                testId={`hub-label-${key}`}
              />
            </group>
            {others.map((d, i) => {
              const pos = positions[i];
              return (
                <React.Fragment key={d.ip}>
                  <QuadraticBezierLine
                    start={hubPos}
                    end={pos}
                    mid={[(hubPos[0] + pos[0]) / 2, 0.8, (hubPos[2] + pos[2]) / 2]}
                    color={selected && selected.ip === d.ip ? color : "#C6CCD6"}
                    lineWidth={selected && selected.ip === d.ip ? 2.4 : 1.2}
                    transparent
                    opacity={0.8}
                  />
                  <DeviceNode3D
                    device={d}
                    position={pos}
                    onSelect={onSelect}
                    selected={selected && selected.ip === d.ip}
                    isNew={newIps && newIps.has(d.ip)}
                    label={label(d)}
                    flagged={isFlagged(d, flags)}
                  />
                </React.Fragment>
              );
            })}
          </group>
        );
      })}

      <ContactShadows position={[0, -0.6, 0]} opacity={0.35} scale={22} blur={2.4} far={5} />

      <OrbitControls
        enablePan={false}
        minDistance={4}
        maxDistance={22}
        maxPolarAngle={Math.PI / 2.05}
        autoRotate={!selected}
        autoRotateSpeed={0.4}
      />
    </Canvas>
  );
}
